import { useNavigation } from '@/app/hooks'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { thermostatEnrollmentCollection } from '@/reducers/deviceProgramEnrollments'
import {
  thermostatPrecoolSavingsSummariesCollection,
  thermostatSavingsSummariesCollection,
} from '@/reducers/thermostatSavingsSummaries'
import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { selectUtilityProgramEnrollmentForUser } from '@/reducers/utilityProgramEnrollments'
import { ThermostatEnrollment } from '@/types/utilityProgramEnrollment'
import {
  Thermostat,
  ThermostatSavingsSummary,
  ThermostatSettings,
} from '@/types'
import { ThermostatPrecoolSavingsSummary } from '@/types/thermostatSavingsSummary'
import { centsToDollars } from '@/utils/currency'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

import { thermostatsCollection } from '@/reducers/thermostats'
import { thermostatCostHistorySummaryCollection } from '@/reducers/thermostatCostHistorySummaryCollection'

import useProgramDisplayType from '@/hooks/useMultiProgramDisplay/useProgramDisplayType'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { useCallback, useEffect, useState } from 'react'
import { debounce } from 'lodash'
import { Props } from './ThermostatDeviceSection'

function usePrefetches(thermostatId: Thermostat['id']) {
  thermostatComfortSettingsCollection.useFetch(
    `/thermostats/${thermostatId}/comfort_settings/`,
  )
}

function useSettingToggle(
  settingKey: keyof ThermostatSettings,
  thermostatId: Thermostat['id'],
  settings: ThermostatSettings | undefined,
  thermostatEnrollment: ThermostatEnrollment | undefined,
  isInInactivePeriod = false,
) {
  const dispatch = useAppDispatch()
  let forcedState = undefined
  if (thermostatEnrollment?.enrolled === true) {
    forcedState = thermostatEnrollment?.group?.[
      settingKey as keyof typeof thermostatEnrollment.group
    ] as boolean | undefined
  }
  const [isToggleOn, setIsToggleOn] = useState(false)

  useEffect(() => {
    if (forcedState !== undefined && forcedState !== null) {
      setIsToggleOn(forcedState)
    } else {
      setIsToggleOn(!!settings?.[settingKey])
    }
  }, [settings, settingKey, forcedState])

  const onToggleChange = useCallback(
    debounce((value: boolean) => {
      setIsToggleOn(value)
      const updatedSettings = { [settingKey]: value }
      dispatch(
        thermostatSettingsCollection.actions.update(
          `/thermostats/${thermostatId}/settings/`,
          updatedSettings,
          { id: settings?.id },
        ),
      )
    }, 200),
    [dispatch, thermostatId, settings?.id, settingKey],
  )

  let rightLabel = 'Off'

  // If we are toggled on, we want to show "On"
  if (isToggleOn) {
    rightLabel = 'On'
  }

  // If we are in an inactive period, we want to show "Inactive"
  if (isInInactivePeriod) {
    rightLabel = 'Inactive'
  }

  return {
    isToggleOn,
    onToggleChange,
    rightLabel,
  }
}

export default function useThermostatDeviceSectionViewModel(props: Props) {
  usePrefetches(props.thermostatId)
  const navigation = useNavigation()

  const utilityEnrollment = useAppSelector(
    selectUtilityProgramEnrollmentForUser,
  )
  const profile = useAppSelector((state) => state.user.user)
  const hasCostCategories =
    (profile?.profile?.plan?.cost_categories?.length ?? 0) > 1
  // Determine if the thermostat is eligible for enrollment in a utility program
  const isEligible =
    utilityEnrollment?.utility_program.device_eligibility?.eligible_thermostat_ids.includes(
      props.thermostatId,
    )
  const isEnrolled = utilityEnrollment?.thermostat_ids?.includes(
    props.thermostatId,
  )
  const validEnrollment = utilityEnrollment && (isEligible || isEnrolled)

  const { data: thermostatEnrollments } =
    thermostatEnrollmentCollection.useFetch()
  const thermostatEnrollment = thermostatEnrollments.find(
    (enrollment) => enrollment.thermostat_id === props.thermostatId,
  )
  const { data: savingsSummaryList } =
    thermostatSavingsSummariesCollection.useFetch(
      `/thermostats/${props.thermostatId}/savings_summary/`,
    )
  const { data: precoolSavingsSummaryList } =
    thermostatPrecoolSavingsSummariesCollection.useFetch(
      `/thermostats/${props.thermostatId}/savings_summary/pre_cool/`,
    )
  const { data: thermostats } = thermostatsCollection.useFetch()
  const thermostat = thermostats.find((t) => t.id === props.thermostatId)

  // Fetch cost history summary for month savings
  const { data: costHistoryList } =
    thermostatCostHistorySummaryCollection.useFetch({
      path: `thermostats/${props.thermostatId}/cost_history/summary`,
    })
  const costHistory = costHistoryList[0]

  const savingsSummary = savingsSummaryList[0] as
    | ThermostatSavingsSummary
    | undefined
  const precoolSavingsSummary = precoolSavingsSummaryList[0] as
    | ThermostatPrecoolSavingsSummary
    | undefined
  const gridSupportSavingsDollars = Math.ceil(
    centsToDollars(savingsSummary?.grid_support_savings_cents ?? 0),
  )
  const precoolSavingsDollars = Math.ceil(
    centsToDollars(precoolSavingsSummary?.pre_cool_savings_cents ?? 0),
  )

  const { programDisplayType } = useProgramDisplayType()

  const {
    data: settingsArray,
    status: settingsStatus,
    error: settingsError,
  } = thermostatSettingsCollection.useFetch(
    `/thermostats/${props.thermostatId}/settings/`,
  )
  const settings = settingsArray[0] as ThermostatSettings | undefined

  const gridSupportSavings =
    savingsSummary && settings?.grid_support_enabled
      ? gridSupportSavingsDollars
      : undefined
  const isEcobee = thermostat?.portal === 'Ecobee'
  const precoolSavings =
    precoolSavingsSummary && settings?.pre_cool_enabled && !isEcobee
      ? precoolSavingsDollars
      : undefined

  const costCategoryToggle = useSettingToggle(
    'cost_category_optimization_enabled',
    props.thermostatId,
    settings,
    thermostatEnrollment,
    thermostat?.is_in_inactive_period_or_comfort_setting,
  )

  let enrollmentLabelText = 'Unenrolled'
  const enableThermostatEnrollment = useFeatureFlag({
    flag: 'thermostat_enrollments',
  })

  if (
    thermostatEnrollment &&
    (thermostatEnrollment.unenrolled_at === null ||
      thermostatEnrollment.unenrolled_at === undefined)
  ) {
    enrollmentLabelText = 'Enrolled'
  }

  // Calculate month savings from cost history
  const monthCostSummary = costHistory?.month
  const monthCostOfOptiwattAdjustmentsCents =
    monthCostSummary?.cost_of_optiwatt_adjustments_cents || 0

  return {
    gridSupportSavings,
    precoolSavings,
    settings: settings,
    settingsError: settingsError,
    utilityEnrollment,
    validEnrollment,
    enrollmentLabelText,
    enableThermostatEnrollment,
    thermostatId: props.thermostatId,
    loading: settingsStatus === 'loading',
    hasCostCategories,
    costCategoryToggle,
    navigateToGridSupport: () => {
      navigation.push(`/devices/thermostat/${props.thermostatId}/grid-support`)
    },
    navigateToPrecoolPreheatSettings: () => {
      navigation.push(
        `/devices/thermostat/${props.thermostatId}/precool-preheat-settings`,
      )
    },
    navigateToUtilityProgramEnrollment: () => {
      navigation.push(
        `/devices/thermostat/${props.thermostatId}/utility-enrollment`,
      )
    },
    navigateToCostCategoryControls: () => {
      navigation.push(
        `/devices/thermostat/${props.thermostatId}/cost-categories`,
      )
    },
    programDisplayType,
    monthCostOfOptiwattAdjustmentsCents,
    isEcobee,
  }
}
